import PropTypes from 'prop-types'
import {HTTPNotFound} from 'pwa-kit-react-sdk/ssr/universal/errors'
import React, {useEffect} from 'react'
import useCachedProduct from '../../hooks/use-cached-product'
import useAsync from '../../commerce-api/hooks/useAsync'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'
import {isError} from '../../commerce-api/utils'

/**
 * Display product tile for a given product ID.
 *
 * @param {string} pid - Product ID
 *
 * @param {string} type - Product tile type, optional
 *
 * @example
 *   <pwa-widget-product pid="101001" type="look">
 *     $include('Widget-Product', 'pid', '#(product.product_id)', 'type', 'plptile')$
 *   </pwa-widget-product>
 */
const WidgetProduct = ({pid, type}) => {
    const getCachedProduct = useCachedProduct()
    const {loading, error, data, execute} = useAsync(async (id) => {
        const product = await getCachedProduct({parameters: {id, allImages: true}})
        // Copied from ProductDetail.getProps
        // The `commerce-isomorphic-sdk` package does not throw errors, so
        // we have to check the returned object type to inconsistencies.
        if (isError(product)) {
            throw new HTTPNotFound(product.detail)
        }
        return product
    })
    const productTileVariant = type === 'look' ? 'Quickshop' : 'Default'

    useEffect(() => {
        if (pid) {
            execute(pid)
        }
    }, [execute, pid])

    useEffect(() => {
        if (!pid) {
            console.error('No product ID provided')
        }
    }, [pid])

    useEffect(() => {
        if (error) {
            console.error('Failed to load product', error)
        }
    }, [error])

    if (!pid) {
        return null
    }

    if (loading) {
        return <ProductTileSkeleton />
    }

    if (error) {
        return null
    }

    // prevent initial flash of undefined product
    if (!data) {
        return null
    }

    return <ProductTile product={data} variant={productTileVariant} showQuickView />
}

WidgetProduct.defaultProps = {
    type: 'default'
}

WidgetProduct.propTypes = {
    pid: PropTypes.string,
    type: PropTypes.string
}

export default WidgetProduct
